import { useCallback, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { SectionTitle } from "@/components/common";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import { Plus, Eye, Pencil, Ban, CheckCircle, Trash2 } from "lucide-react";
import CustomerForm from "@/components/customers/CustomerForm";

const STATUS_CLS = {
  ACTIVE: "bg-signal-green/15 text-signal-green",
  INACTIVE: "bg-muted text-muted-foreground",
  BLOCKED: "bg-signal-red/15 text-signal-red",
};

export default function Customers() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [rows, setRows] = useState([]);
  const [search, setSearch] = useState("");
  const [status, setStatus] = useState("ALL");
  const [formOpen, setFormOpen] = useState(false);
  const [editing, setEditing] = useState(null);

  const canAdd = ["admin", "sales"].includes(user?.role);
  const canEdit = ["admin", "sales", "accounts"].includes(user?.role);
  const isAdmin = user?.role === "admin";

  const load = useCallback(async () => {
    const q = [];
    if (search) q.push(`search=${encodeURIComponent(search)}`);
    if (status !== "ALL") q.push(`status=${status}`);
    const r = await api.get(`/customers${q.length ? "?" + q.join("&") : ""}`);
    setRows(r.data);
  }, [search, status]);

  useEffect(() => { const t = setTimeout(load, 250); return () => clearTimeout(t); }, [load]);

  const setStatusApi = async (code, st) => {
    try {
      await api.post(`/customers/${code}/status`, { status: st });
      toast.success(`${code} → ${st}`);
      load();
    } catch (e) {
      toast.error(typeof e.response?.data?.detail === "string" ? e.response.data.detail : "Failed");
    }
  };

  const del = async (c) => {
    if (!window.confirm("Are you sure you want to permanently delete this customer?")) return;
    try {
      await api.delete(`/customers/${c.code}`);
      toast.success(`Customer ${c.code} deleted`);
      load();
    } catch (e) {
      toast.error(typeof e.response?.data?.detail === "string" ? e.response.data.detail : "Delete failed");
    }
  };

  return (
    <div className="space-y-5" data-testid="customers-page">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <SectionTitle>Customer Master</SectionTitle>
        {canAdd && (
          <Button onClick={() => { setEditing(null); setFormOpen(true); }} data-testid="add-customer-button" className="bg-primary hover:bg-primary/90 text-primary-foreground">
            <Plus className="w-4 h-4 mr-1" /> ADD CUSTOMER
          </Button>
        )}
      </div>

      <div className="flex gap-3 flex-wrap">
        <Input className="max-w-sm" placeholder="Search code / name / GSTIN / mobile / PAN / sales person" data-testid="customer-search-input"
          value={search} onChange={(e) => setSearch(e.target.value)} />
        <Select value={status} onValueChange={setStatus}>
          <SelectTrigger className="w-40" data-testid="customer-status-filter"><SelectValue /></SelectTrigger>
          <SelectContent>
            {["ALL", "ACTIVE", "INACTIVE", "BLOCKED"].map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}
          </SelectContent>
        </Select>
      </div>

      <div className="bg-card border border-border rounded-lg overflow-x-auto">
        <table className="w-full text-sm" data-testid="customers-table">
          <thead>
            <tr className="border-b border-border text-left">
              {["Code", "Name", "Type", "Contact", "GSTIN", "State", "Sales Person", "Status", "Actions"].map((h) => (
                <th key={h} className="px-4 py-3 text-[10px] font-mono uppercase tracking-widest text-muted-foreground whitespace-nowrap">{h}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {rows.map((c) => (
              <tr key={c.code} className="border-b border-border/50" data-testid={`customer-row-${c.code}`}>
                <td className="px-4 py-3 metric font-bold text-primary">{c.code}</td>
                <td className="px-4 py-3 font-medium">{c.name}</td>
                <td className="px-4 py-3 text-xs">{c.customer_type || "—"}</td>
                <td className="px-4 py-3 text-xs">{c.billing?.contact || "—"}<br /><span className="metric text-muted-foreground">{c.billing?.mobile || ""}</span></td>
                <td className="px-4 py-3 metric text-xs">{c.billing?.gstin || "—"}</td>
                <td className="px-4 py-3 text-xs">{c.billing?.state || c.state || "—"}</td>
                <td className="px-4 py-3 text-xs">{c.sales_person || "—"}</td>
                <td className="px-4 py-3">
                  <span className={`text-[10px] font-mono uppercase tracking-widest px-1.5 py-0.5 rounded ${STATUS_CLS[c.status] || STATUS_CLS.ACTIVE}`} data-testid={`customer-status-${c.code}`}>{c.status || "ACTIVE"}</span>
                </td>
                <td className="px-4 py-3">
                  <div className="flex gap-1">
                    <Button size="sm" variant="ghost" onClick={() => navigate(`/customers/${c.code}`)} data-testid={`view-${c.code}`}><Eye className="w-4 h-4" /></Button>
                    {canEdit && <Button size="sm" variant="ghost" onClick={() => { setEditing(c); setFormOpen(true); }} data-testid={`edit-${c.code}`}><Pencil className="w-4 h-4" /></Button>}
                    {isAdmin && (c.status === "ACTIVE" ? (
                      <Button size="sm" variant="ghost" title="Deactivate" onClick={() => setStatusApi(c.code, "INACTIVE")} data-testid={`deactivate-${c.code}`}><Ban className="w-4 h-4 text-signal-amber" /></Button>
                    ) : (
                      <Button size="sm" variant="ghost" title="Reactivate" onClick={() => setStatusApi(c.code, "ACTIVE")} data-testid={`reactivate-${c.code}`}><CheckCircle className="w-4 h-4 text-signal-green" /></Button>
                    ))}
                    {isAdmin && <Button size="sm" variant="ghost" title="Delete" onClick={() => del(c)} data-testid={`delete-${c.code}`}><Trash2 className="w-4 h-4 text-signal-red" /></Button>}
                  </div>
                </td>
              </tr>
            ))}
            {rows.length === 0 && <tr><td colSpan={9} className="px-4 py-8 text-center text-muted-foreground" data-testid="customers-empty">No customers found</td></tr>}
          </tbody>
        </table>
      </div>

      <CustomerForm open={formOpen} onOpenChange={setFormOpen} customer={editing} onSaved={() => load()} />
    </div>
  );
}
